import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { useEditorStore } from "@/store/editorStore";
import { CitationNodeView } from "@/components/editor/nodeviews/CitationNodeView";

export interface CitationOptions {
  HTMLAttributes: Record<string, unknown>;
}

/**
 * Citation mode → natbib command on export (content-converter):
 *   parenthetical → \citep   textual → \citet
 *   author → \citeauthor     year → \citeyear     bare → \cite
 * Parenthetical is the default for new citations (the most common form).
 * `bare` is reserved for round-tripping legacy `\cite{}` unchanged.
 */
export type CitationMode = "parenthetical" | "textual" | "author" | "year" | "bare";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    citation: {
      /**
       * Insert a citation in the given mode (default: parenthetical).
       */
      insertCitation: (citeKey: string, mode?: CitationMode) => ReturnType;
    };
  }
}

/**
 * TipTap extension for inline citations
 * Supports @cite{key} syntax for bibliography references
 */
export const Citation = Node.create<CitationOptions>({
  name: "citation",
  group: "inline",
  inline: true,
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      citeKey: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-cite-key") || "",
        renderHTML: (attributes) => ({
          "data-cite-key": attributes.citeKey,
        }),
      },
      // Citation mode — maps to the natbib command on export. Defaults to
      // parenthetical for freshly inserted citations; the content parser sets
      // "bare" when reconstructing a legacy `\cite{}` so it round-trips intact.
      mode: {
        default: "parenthetical",
        parseHTML: (element) => element.getAttribute("data-mode") || "parenthetical",
        renderHTML: (attributes) => ({
          "data-mode": attributes.mode,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="citation"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const citeKey = node.attrs.citeKey as string;

    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "citation",
        "data-cite-key": citeKey,
        class: "inline-citation",
        title: `Citation: ${citeKey}`,
      }),
      `[${citeKey}]`,
    ];
  },

  addCommands() {
    return {
      insertCitation:
        (citeKey: string, mode: CitationMode = "parenthetical") =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { citeKey, mode },
          });
        },
    };
  },

  // React NodeView — renders a rich pill with hover popover + unresolved
  // warning state. Replaces the static "[key]" span from renderHTML when
  // the node is rendered in an active ProseMirror view. renderHTML is
  // still used for read-only serialisation (exports, share views).
  addNodeView() {
    return ReactNodeViewRenderer(CitationNodeView);
  },

  addProseMirrorPlugins() {
    const citeRegex = /@cite\{([^}]+)\}/g;

    return [
      // Decoration plugin: renders citation highlights in the editor
      new Plugin({
        key: new PluginKey("citationInputRule"),
        props: {
          decorations: (state) => {
            const decorations: Decoration[] = [];
            const doc = state.doc;

            doc.descendants((node, pos) => {
              if (node.isText && node.text) {
                const text = node.text;
                let match: RegExpExecArray | null;

                citeRegex.lastIndex = 0;
                while ((match = citeRegex.exec(text)) !== null) {
                  const start = pos + match.index;
                  const end = start + match[0].length;
                  const citeKey = match[1];

                  decorations.push(
                    Decoration.inline(start, end, {
                      class: "citation-highlight",
                      "data-cite-key": citeKey,
                    })
                  );
                }
              }
            });

            return DecorationSet.create(doc, decorations);
          },

          // Handle clicks on citation decorations
          handleClick: (view, pos, event) => {
            const target = event.target as HTMLElement;
            if (!target.classList.contains("citation-highlight") && !target.classList.contains("inline-citation")) {
              return false;
            }

            const citeKey = target.getAttribute("data-cite-key");
            if (citeKey) {
              useEditorStore.getState().setHighlightedCiteKey(citeKey);
              return true;
            }
            return false;
          },
        },
      }),

    ];
  },
});

export default Citation;
